import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSidebarItems } from './config/SidebarItems';
import { menuLabel, sidebarTabLabel, sidebarWorkspaceLabel, viewLabel } from '../../i18n/labels';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getFavorites, getCachedFavorites, reconstructFavoriteIcon } from '../../utils/favorites';
import FavoritesManager from './FavoritesManager';
import { CogIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, ChevronDownIcon } from '../ui/Icons';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { COLLAPSED_RAIL_WIDTH_PX } from '../../lib/layoutConstants';
import AppSidebarFlyout from './sidebar/AppSidebarFlyout';
import AppSidebarViewsMenu from './sidebar/AppSidebarViewsMenu';
import AppSidebarRailButton from './sidebar/AppSidebarRailButton';
import {
  SIDEBAR_EXPANDED_WIDTH,
  sidebarCollapseToggleClass,
  sidebarFooterClass,
  sidebarHeaderClass,
  sidebarIconWellClass,
  sidebarNavChildClass,
  sidebarNavGroupClass,
  sidebarNavItemClass,
  sidebarNavScrollClass,
  sidebarShellClass,
  sidebarUserButtonClass,
} from './sidebar/sidebarDesign';
import api from '../../services/api';
import useSWR from 'swr';
import { APP_BRAND } from '../../config/branding';
import AppBrandIcon from '../branding/AppBrandIcon';
import UserAvatar, { getUserDisplayName } from '../users/UserAvatar';
import type { User } from '../../contexts/AuthContext';
import {
  appointmentsCalendarPath,
  isAppointmentsSidebarActive,
  isTodosSidebarActive,
} from '../../modules/crm-appointments/paths';
import { todosSidebarPath } from '../../modules/crm-todos/paths';

// Helper functions for view preferences using API
const viewPreferencesFetcher = async (url: string) => {
  const response = await api.get(url);
  return response.data.data || {};
};

const getViewPreference = (moduleName: string, preferences: Record<string, string> | null): string | null => {
  if (!preferences) return null;
  return preferences[moduleName] || null;
};

const saveViewPreference = async (moduleName: string, viewType: string, mutate: () => Promise<any>): Promise<void> => {
  try {
    await api.patch('/settings/user/view-preferences', {
      preferences: {
        [moduleName]: viewType,
      },
    });
    // Refresh the preferences cache
    await mutate();
  } catch (e) {
    console.error('Failed to save view preference:', e);
  }
};

export interface MenuItemView {
  type: 'list' | 'kanban' | 'card' | 'calendar' | 'table' | 'templates' | 'grouped' | 'tree' | 'summary' | 'daily' | 'daily_2' | 'analytics' | 'categories' | 'yearly' | 'monthly';
  label: string;
  path: string;
  icon: React.ReactNode;
}

export interface MenuItem {
  name: string;
  path?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  views?: MenuItemView[];
  badge?: number;
  /** When set, item is shown only when this module group is enabled in entitlements. */
  moduleGroupKey?: string;
  /** When false, group starts collapsed (still auto-expands when a child route is active). */
  defaultExpanded?: boolean;
  /** Permission module key for view check; if not set, derived from path when filtering by permissions. */
  permissionModuleKey?: string;
  /** When set, item is shown if the user has view on any listed module. */
  permissionModuleKeys?: string[];
  /** When true, item is shown only if user has edit permission on the module (not just view). */
  requiresEdit?: boolean;
}

// Toggle View Button Component
interface ToggleViewButtonProps {
  views: MenuItemView[];
  currentPath: string;
  onViewChange: (path: string) => Promise<void>;
  colors: any;
  collapsed?: boolean;
  moduleName: string;
  viewPreferences?: Record<string, string>;
  mutateViewPreferences?: () => Promise<any>;
}

const ToggleViewButton: React.FC<ToggleViewButtonProps> = ({
  views,
  currentPath,
  onViewChange,
  colors,
  collapsed = false,
  moduleName,
  viewPreferences = {},
  mutateViewPreferences,
}) => {
  const { t } = useTranslation('sidebar');
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Find current view based on path, or use saved preference
  const getCurrentView = () => {
    const savedViewType = getViewPreference(moduleName, viewPreferences);
    const exactPathMatch = views.find(v => currentPath === v.path);
    const pathMatch = views.find(v =>
      currentPath.startsWith(v.path + '/')
    );

    // PRIORITY 1: If we're on a view's exact path (not a detail page), that view is current so only it gets the tick
    if (exactPathMatch && !pathMatch) {
      return exactPathMatch;
    }

    // PRIORITY 2: Saved preference (for detail pages, dashboard, or when path doesn't match a view)
    if (savedViewType) {
      const savedView = views.find(v => v.type === savedViewType);
      if (savedView) {
        return savedView;
      }
    }

    // PRIORITY 3: Path prefix match (e.g. detail page under a view)
    if (pathMatch) {
      return pathMatch;
    }

    return views[0];
  };

  const currentView = getCurrentView();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (collapsed) {
    return (
      <button
        onClick={async (e) => {
          e.stopPropagation();
          if (views.length > 1 && mutateViewPreferences) {
            // Cycle through views
            const currentIndex = views.findIndex(v => v === currentView);
            const nextIndex = (currentIndex + 1) % views.length;
            const nextView = views[nextIndex];
            // Save preference when cycling
            await saveViewPreference(moduleName, nextView.type, mutateViewPreferences);
            await onViewChange(nextView.path);
          }
        }}
        className={cn(
          'p-1.5 rounded-lg transition-colors',
          colors.sidebarText,
          colors.sidebarItemHover
        )}
        title={`Switch view (${currentView.label})`}
      >
        {currentView.icon}
      </button>
    );
  }

  if (views.length <= 1) {
    return null;
  }

  return (
    <div className="relative" ref={buttonRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors text-xs',
          colors.sidebarText,
          colors.sidebarItemHover
        )}
        title={currentView.label}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          {currentView.icon}
        </span>
        <ChevronDownIcon className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-1 w-48 rounded-lg shadow-lg border',
            colors.sidebarBackground,
            colors.border
          )}
          style={{ top: '100%', ...zStyle(Z_INDEX.localDropdown) }}
        >
          <div className="py-1">
            {views.map((view, index) => {
              // Compare by path so only the current view shows the tick (e.g. Monthly View vs Monthly Report)
              const isActive = view.path === currentView.path;
              return (
                <button
                  key={index}
                  onClick={async (e) => {
                    e.stopPropagation();
                    // Save preference when selecting a view
                    if (mutateViewPreferences) {
                      await saveViewPreference(moduleName, view.type, mutateViewPreferences);
                    }
                    await onViewChange(view.path);
                    setIsOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? `${colors.sidebarActive} ${colors.sidebarActiveText}`
                      : `${colors.sidebarText} ${colors.sidebarItemHover}`
                  )}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    {view.icon}
                  </span>
                  <span className="flex-1 text-left">{viewLabel(t, view.label)}</span>
                  {isActive && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface MenuItemComponentProps {
  item: MenuItem;
  level: number;
  location: ReturnType<typeof useLocation>;
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
  isFavorites?: boolean;
  onManageFavorites?: () => void;
  collapsed?: boolean;
  onItemClick?: (item: MenuItem, hasSubitems: boolean) => void;
  viewPreferences?: Record<string, string>;
  mutateViewPreferences?: () => Promise<any>;
  /** When set, only this item is active if it is the best match (longest path); prevents parent path from being active when a child path matches. */
  activePathAmongSiblings?: string;
  /** Collapsed rail right edge (px) for portaled view flyouts */
  railRightPx?: number;
}

const MenuItemComponent: React.FC<MenuItemComponentProps> = ({
  item,
  level,
  location,
  isMobileOpen,
  setIsMobileOpen,
  isFavorites = false,
  onManageFavorites,
  collapsed = false,
  onItemClick,
  viewPreferences = {},
  mutateViewPreferences,
  activePathAmongSiblings,
  railRightPx = 64,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('sidebar');
  const itemLabel = menuLabel(t, item.name);
  const navigate = useNavigate();
  const collapsedBtnRef = useRef<HTMLButtonElement>(null);
  const viewsFlyoutLeaveTimer = useRef<number | null>(null);
  const [viewsFlyoutOpen, setViewsFlyoutOpen] = useState(false);
  const [viewsFlyoutPos, setViewsFlyoutPos] = useState({ top: 0, left: railRightPx });
  const hasChildren = item.children && item.children.length > 0;
  const hasViews = item.views && item.views.length > 0;
  const [isExpanded, setIsExpanded] = useState(
    item.defaultExpanded === false ? false : (isFavorites && hasChildren)
  );
  
  // Get preferred view path or default to first view
  const getPreferredViewPath = (): string | undefined => {
    if (!hasViews || !item.views) return undefined;
    const savedViewType = getViewPreference(item.name, viewPreferences);
    if (savedViewType) {
      // Special case for Emails module - reconstruct path from view type
      if (item.name === 'Emails') {
        return '/emails?view=card';
      }
      // Find view by type and return its path
      const savedView = item.views.find(view => view.type === savedViewType);
      if (savedView) {
        return savedView.path;
      }
    }
    // Default to first view
    return item.views[0].path;
  };
  
  // Determine current path - use preferred view path if no direct path
  const itemPath = item.path || (hasViews ? getPreferredViewPath() : undefined);

  const navigationPath =
    item.name === 'Appointments'
      ? appointmentsCalendarPath()
      : item.name === 'Todos'
        ? todosSidebarPath()
        : itemPath || '#';

  // Check if current path or any child path is active; when activePathAmongSiblings is set, only active if this item is the best match (so parent path like /settings is not active when /settings/offer-pdf-layouts is active)
  let pathMatches = itemPath
    ? location.pathname === itemPath || location.pathname.startsWith(itemPath + '/')
    : false;

  if (item.name === 'Appointments') {
    pathMatches = isAppointmentsSidebarActive(location.pathname, location.search);
  } else if (item.name === 'Todos' && itemPath) {
    pathMatches = isTodosSidebarActive(location.pathname, location.search, itemPath);
  }
  const isActive = Boolean(
    pathMatches &&
      (!activePathAmongSiblings ||
        activePathAmongSiblings === itemPath ||
        (hasViews &&
          item.views!.some(
            (v) =>
              activePathAmongSiblings === v.path ||
              (v.path && activePathAmongSiblings.startsWith(`${v.path}/`)),
          ))),
  );
  
  // Check if any child is active (by its own path OR any of its views' paths)
  const hasActiveChild = hasChildren
    ? item.children!.some((child) => {
        // Direct child path
        if (child.path) {
          if (location.pathname === child.path || location.pathname.startsWith(child.path + '/')) {
            return true;
          }
        }

        // Any of the child's views
        if (child.views && child.views.length > 0) {
          return child.views.some((view) => {
            return (
              location.pathname === view.path ||
              location.pathname.startsWith(view.path + '/')
            );
          });
        }

        return false;
      })
    : false;
  
  // Check if any view is active
  const hasActiveView = hasViews
    ? item.views!.some((view) => {
        return location.pathname === view.path || location.pathname.startsWith(view.path + '/');
      })
    : false;

  // Auto-expand if has active child or if it's Favorites with children (use startTransition to avoid freezing when expanding large trees)
  React.useEffect(() => {
    if (hasActiveChild) {
      React.startTransition(() => setIsExpanded(true));
    } else if (isFavorites && hasChildren) {
      React.startTransition(() => setIsExpanded(true));
    }
  }, [hasActiveChild, isFavorites, hasChildren]);

  // Check if we're on the base path (not a specific view) and should redirect to preferred view
  // This hook must be called unconditionally (React rules) - placed here before any conditional returns
  React.useEffect(() => {
    if (hasViews && item.views && item.path) {
      const savedViewType = getViewPreference(item.name, viewPreferences);
      let preferredPath: string | undefined;
      
      if (savedViewType) {
        // Special case for Emails module - reconstruct path from view type
        if (item.name === 'Emails') {
          preferredPath = '/emails?view=card';
        } else {
          // Find view by type and return its path
          const savedView = item.views.find(view => view.type === savedViewType);
          preferredPath = savedView?.path;
        }
      } else {
        // Default to first view if no preference
        preferredPath = item.views[0]?.path;
      }
      
      // Only redirect when on the base path AND current path is not already a view path (e.g. user clicked "List view" from calendar -> stay on /appointments)
      const currentPathIsAViewPath = item.views.some((v) => v.path === location.pathname);
      if (preferredPath && location.pathname === item.path && preferredPath !== item.path && !currentPathIsAViewPath) {
        // We're on the base path but have a preferred view - navigate to it
        navigate(preferredPath, { replace: true });
      }
    }
  }, [hasViews, item.views, item.path, item.name, location.pathname, viewPreferences, navigate]);

  // Handle view toggle
  const handleViewChange = async (path: string) => {
    // Save the view preference (find view by path and save its type)
    if (hasViews && item.views && mutateViewPreferences) {
      const view = item.views.find(v => v.path === path);
      if (view) {
        await saveViewPreference(item.name, view.type, mutateViewPreferences);
      }
    }
    navigate(path);
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const paddingLeft = collapsed ? 0 : level * 16 + 16;

  const handleClick = (e: React.MouseEvent) => {
    if (collapsed && hasChildren) {
      e.preventDefault();
      e.stopPropagation();
      if (onItemClick) {
        onItemClick(item, true);
      }
    } else if (hasChildren) {
      e.preventDefault();
      React.startTransition(() => {
        setIsExpanded(!isExpanded);
      });
    }
  };

  const openViewsFlyout = useCallback(() => {
    if (viewsFlyoutLeaveTimer.current != null) {
      window.clearTimeout(viewsFlyoutLeaveTimer.current);
      viewsFlyoutLeaveTimer.current = null;
    }
    const r = collapsedBtnRef.current?.getBoundingClientRect();
    setViewsFlyoutPos({ top: r?.top ?? 0, left: r?.right ?? railRightPx });
    setViewsFlyoutOpen(true);
  }, [railRightPx]);

  const scheduleCloseViewsFlyout = useCallback(() => {
    viewsFlyoutLeaveTimer.current = window.setTimeout(() => setViewsFlyoutOpen(false), 120);
  }, []);

  useEffect(() => {
    return () => {
      if (viewsFlyoutLeaveTimer.current != null) {
        window.clearTimeout(viewsFlyoutLeaveTimer.current);
      }
    };
  }, []);

  // For Favorites section, always show as expandable (even if empty)
  // For items with views, show as regular item with toggle button
  const shouldShowAsExpandable = (hasChildren || isFavorites) && !hasViews;

  if (shouldShowAsExpandable) {
    return (
      <div>
        <div className="flex items-center">
          {collapsed ? (
            <AppSidebarRailButton
              label={itemLabel}
              active={isActive}
              onClick={handleClick}
              badge={item.badge}
            >
              {item.icon}
            </AppSidebarRailButton>
          ) : (
            <>
              <button
                onClick={handleClick}
                className={cn('group flex-1', sidebarNavItemClass(isActive))}
                style={{ paddingLeft: `${paddingLeft}px` }}
              >
                {item.icon && (
                  <span className={sidebarIconWellClass(isActive)}>{item.icon}</span>
                )}
                <span className="flex-1 text-left">{itemLabel}</span>
                {item.badge && (
                  <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
                <svg
                  className={cn('ml-2 h-4 w-4 transition-transform duration-200', isExpanded ? 'rotate-90' : '')}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              {isFavorites && onManageFavorites && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManageFavorites();
                  }}
                  className="mr-1 rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                  title={t('chrome.manageFavorites')}
                >
                  <CogIcon className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
        {!collapsed && isExpanded && (
          <div className="mt-1 space-y-1">
            {hasChildren && item.children!.length > 0 ? (() => {
              const pathname = location.pathname;
              const siblingPaths: string[] = [];
              item.children!.forEach((child) => {
                if (child.path) siblingPaths.push(child.path);
                child.views?.forEach((v) => { if (v.path) siblingPaths.push(v.path); });
              });
              const bestMatch = siblingPaths
                .filter((p) => pathname === p || pathname.startsWith(p + '/'))
                .sort((a, b) => b.length - a.length)[0];
              return item.children!.map((child, index) => (
                <MenuItemComponent
                  key={child.path || `${item.name}-child-${index}`}
                  item={child}
                  level={level + 1}
                  location={location}
                  isMobileOpen={isMobileOpen}
                  setIsMobileOpen={setIsMobileOpen}
                  collapsed={collapsed}
                  onItemClick={onItemClick}
                  viewPreferences={viewPreferences}
                  mutateViewPreferences={mutateViewPreferences}
                  activePathAmongSiblings={bestMatch}
                />
              ));
            })(            ) : isFavorites ? (
              <div className="px-4 py-2 text-xs text-slate-500" style={{ paddingLeft: `${paddingLeft + 16}px` }}>
                No favorites yet. Click the settings icon to add some.
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  if (collapsed) {
    const showViewsFlyout = hasViews && item.views && item.views.length > 1;
    return (
      <>
        <AppSidebarRailButton
          buttonRef={collapsedBtnRef}
          label={itemLabel}
          active={isActive || hasActiveView}
          badge={item.badge}
          onClick={() => {
            if (hasViews && item.views) {
              const preferredPath = getPreferredViewPath();
              navigate(preferredPath || item.views[0].path);
            } else if (onItemClick) {
              onItemClick(item, false);
            }
          }}
          onMouseEnter={showViewsFlyout ? openViewsFlyout : undefined}
          onMouseLeave={showViewsFlyout ? scheduleCloseViewsFlyout : undefined}
        >
          {item.icon}
        </AppSidebarRailButton>
        {showViewsFlyout && (
          <AppSidebarViewsMenu
            open={viewsFlyoutOpen}
            top={viewsFlyoutPos.top}
            left={viewsFlyoutPos.left}
            views={item.views!}
            currentPath={location.pathname}
            onSelect={async (path) => {
              await handleViewChange(path);
              setViewsFlyoutOpen(false);
            }}
            onMouseEnter={openViewsFlyout}
            onMouseLeave={scheduleCloseViewsFlyout}
          />
        )}
      </>
    );
  }

  // Item with views - show toggle button
  if (hasViews && item.views) {
    const preferredPath = getPreferredViewPath();
    const defaultPath = preferredPath || item.views[0].path;
    
    return (
      <div
        className={cn('group relative flex items-center', sidebarNavItemClass(isActive || hasActiveView))}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <button
          onClick={async () => {
            await handleViewChange(defaultPath);
            setIsMobileOpen(false);
          }}
          className="flex w-full flex-1 items-center gap-3 text-left"
        >
          {item.icon && <span className={sidebarIconWellClass(isActive || hasActiveView)}>{item.icon}</span>}
          <span className="flex-1">{itemLabel}</span>
        </button>
        {item.badge && (
          <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
            {item.badge > 9 ? '9+' : item.badge}
          </span>
        )}
        <ToggleViewButton
          views={item.views}
          currentPath={location.pathname}
          onViewChange={handleViewChange}
          colors={colors}
          moduleName={item.name}
          viewPreferences={viewPreferences}
          mutateViewPreferences={mutateViewPreferences}
        />
      </div>
    );
  }

  return (
    <Link
      to={navigationPath}
      onClick={() => setIsMobileOpen(false)}
      className={cn(
        'group relative flex items-center gap-3',
        level > 0 ? sidebarNavChildClass(isActive) : sidebarNavItemClass(isActive),
      )}
      style={{ paddingLeft: level > 0 ? `${paddingLeft}px` : `${paddingLeft}px` }}
    >
      {item.icon && (
        <span className={level > 0 ? 'flex h-5 w-5 shrink-0 items-center justify-center text-slate-400' : sidebarIconWellClass(isActive)}>
          {item.icon}
        </span>
      )}
      <span className="flex-1">{itemLabel}</span>
      {item.badge && (
        <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </Link>
  );
};

interface SidebarProps {
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
}

// Valid view types for each module (must match backend)
const validViewTypes: Record<string, string[]> = {
  'Todos': ['list', 'monthly'],
  'Address Book': ['list'],
  'Emails': ['card'],
  'Appointments': ['list', 'calendar'],
};

// Validate view preferences - backend now returns all preferences with defaults
const validateViewPreferences = (prefs: Record<string, string> | null): Record<string, string> => {
  if (!prefs) {
    // If no preferences, return defaults (backend should have set these on login)
    const defaults: Record<string, string> = {};
    for (const [moduleName, validTypes] of Object.entries(validViewTypes)) {
      if (validTypes.length > 0) {
        defaults[moduleName] = validTypes[0];
      }
    }
    return defaults;
  }
  
  // Backend returns all preferences with defaults, so use as-is
  // Only filter out invalid values as a safety check
  const validated: Record<string, string> = {};
  for (const [moduleName, value] of Object.entries(prefs)) {
    const validTypes = validViewTypes[moduleName];
    if (validTypes && validTypes.includes(value)) {
      validated[moduleName] = value;
    } else if (validTypes && validTypes.length > 0) {
      // If invalid, use default (backend should have fixed this, but safety check)
      validated[moduleName] = validTypes[0];
    }
  }
  
  return validated;
};

const SignOutIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

interface SidebarSignOutFooterProps {
  collapsed: boolean;
  onLogout: () => void | Promise<void>;
}

interface SidebarUserAccountProps {
  user: User;
  collapsed: boolean;
  onOpenProfile: () => void;
}

const SidebarUserAccount: React.FC<SidebarUserAccountProps> = ({ user, collapsed, onOpenProfile }) => {
  const displayName = getUserDisplayName(user);

  if (collapsed) {
    return (
      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={onOpenProfile}
          className={sidebarUserButtonClass(true)}
          title={displayName}
          aria-label={displayName}
        >
          <UserAvatar
            userId={user.id}
            photoPath={user.userPhotoPath}
            userFirstName={user.userFirstName}
            userLastName={user.userLastName}
            userEmail={user.userEmail}
            userDisplayName={user.userDisplayName}
            size="sm"
            rounded="xl"
          />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 pb-2">
      <button
        type="button"
        onClick={onOpenProfile}
        className={sidebarUserButtonClass(false)}
      >
        <UserAvatar
          userId={user.id}
          photoPath={user.userPhotoPath}
          userFirstName={user.userFirstName}
          userLastName={user.userLastName}
          userEmail={user.userEmail}
          userDisplayName={user.userDisplayName}
          size="sm"
          rounded="xl"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{displayName}</p>
          <p className="truncate text-xs text-slate-500">{user.userEmail}</p>
        </div>
      </button>
    </div>
  );
};

const SidebarSignOutFooter: React.FC<SidebarSignOutFooterProps> = ({ collapsed, onLogout }) => {
  const { t } = useTranslation('sidebar');
  const signOutLabel = t('chrome.signOut');
  return (
    <div className={sidebarFooterClass(collapsed)}>
      <button
        type="button"
        onClick={() => void onLogout()}
        title={signOutLabel}
        aria-label={signOutLabel}
        className={cn(
          'group flex w-full items-center rounded-xl text-slate-300 transition-colors hover:bg-red-500/15 hover:text-red-200',
          collapsed ? 'justify-center p-2' : 'gap-2.5 px-2.5 py-2 text-sm',
        )}
      >
        <span
          className={cn(
            'flex items-center justify-center rounded-lg bg-white/[0.05] transition-colors group-hover:bg-red-500/20',
            collapsed ? 'h-10 w-10' : 'h-8 w-8',
          )}
        >
          <SignOutIcon className="h-4 w-4 text-red-400 group-hover:text-red-300" />
        </span>
        {!collapsed && <span>{signOutLabel}</span>}
      </button>
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, setIsMobileOpen }) => {
  const { t } = useTranslation('sidebar');
  const { colors, sidebarCollapsed, setSidebarCollapsed } = useTheme();
  const { appConfig } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarStyle = appConfig.layout.sidebarStyle;
  const isRail = sidebarStyle === 'rail';
  const isTabs = sidebarStyle === 'tabs';
  const isWorkspaces = sidebarStyle === 'workspaces';
  const collapsed = isRail ? true : sidebarCollapsed;
  const asideRef = useRef<HTMLElement | null>(null);
  const [railRightPx, setRailRightPx] = useState(64);
  const [favoritesManagerOpen, setFavoritesManagerOpen] = useState(false);

  // Drawer state for collapsed mode
  const [activeDrawer, setActiveDrawer] = useState<'level2' | 'level3' | 'level4' | null>(null);
  const [drawerData, setDrawerData] = useState<{
    level2: MenuItem | null;
    level3: MenuItem | null;
    level4: MenuItem | null;
  }>({
    level2: null,
    level3: null,
    level4: null,
  });
  const [drawerHistory, setDrawerHistory] = useState<Array<{ level: string; data: MenuItem }>>([]);

  useLayoutEffect(() => {
    if (!collapsed) return;
    const update = () => {
      const el = asideRef.current;
      setRailRightPx(el ? el.getBoundingClientRect().right : 64);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [collapsed, activeDrawer]);

  // Fetch view preferences from API - revalidate on mount to ensure fresh data after login
  const { data: rawViewPreferences, mutate: mutateViewPreferences } = useSWR<Record<string, string>>(
    '/settings/user/view-preferences',
    viewPreferencesFetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      revalidateOnMount: true, // Always revalidate when component mounts (e.g., after login)
      refreshInterval: 0, // Don't auto-refresh, but allow manual refresh
    }
  );

  // Validate view preferences
  const viewPreferences = React.useMemo(() => {
    return validateViewPreferences(rawViewPreferences || null);
  }, [rawViewPreferences]);

  // Listen for view preferences updates from Preferences page and login
  React.useEffect(() => {
    const handleViewPreferencesUpdate = () => {
      // Force revalidation of view preferences
      mutateViewPreferences();
    };

    window.addEventListener('viewPreferencesUpdated', handleViewPreferencesUpdate);
    
    // Also listen for authentication state changes to refresh preferences after login
    const handleAuthChange = () => {
      // Small delay to ensure backend validation has completed
      setTimeout(() => {
        mutateViewPreferences();
      }, 500);
    };
    
    window.addEventListener('userAuthenticated', handleAuthChange);
    
    return () => {
      window.removeEventListener('viewPreferencesUpdated', handleViewPreferencesUpdate);
      window.removeEventListener('userAuthenticated', handleAuthChange);
    };
  }, [mutateViewPreferences]);
  
  const { user, can, permissionsLoaded, logout } = useAuth();
  const userRole = user?.role;
  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const canEdit = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'edit') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const baseMenuItems = useMemo(
    () => getSidebarItems(userRole, canView, canEdit),
    [userRole, canView, canEdit]
  );

  // Stable key so effect only runs when menu structure actually changes (avoids infinite loop from new baseMenuItems reference every render)
  const baseMenuItemsKey = useMemo(
    () => JSON.stringify(baseMenuItems.map((i) => ({ name: i.name, path: i.path, childCount: i.children?.length ?? 0 }))),
    [baseMenuItems]
  );

  // Initialize with base menu items
  const [menuItemsWithFavorites, setMenuItemsWithFavorites] = useState<MenuItem[]>([]);

  // Initialize favorites from cache immediately, then fetch fresh from API. Depend on baseMenuItemsKey so we only re-run when menu structure changes.
  useEffect(() => {
    // Helper to update menu items with favorites
    const applyFavoritesToMenu = (favorites: { title: string; to: string; fullPath?: string | null }[]) => {
      const favoritesWithIcons: MenuItem[] = favorites.map((fav) => {
        const icon = reconstructFavoriteIcon(fav, baseMenuItems);
        return {
          name: fav.title,
          path: fav.to,
          icon: icon,
        };
      });

      const updatedMenuItems = baseMenuItems.map((item) => {
        if (item.name === 'Favorites') {
          return {
            ...item,
            children: favoritesWithIcons,
          };
        }
        return item;
      });

      setMenuItemsWithFavorites(updatedMenuItems);
    };

    // First, load from cache for instant display
    const cachedFavorites = getCachedFavorites();
    if (cachedFavorites.length > 0) {
      applyFavoritesToMenu(cachedFavorites);
    }

    // Then fetch fresh data from API
    const updateFavorites = async () => {
      try {
        const favorites = await getFavorites();
        applyFavoritesToMenu(favorites);
      } catch (error) {
        console.error('Error loading favorites:', error);
        // Keep cached favorites on error - don't clear
      }
    };

    updateFavorites();

    const handleFavoritesUpdate = () => {
      updateFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);

    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, [baseMenuItemsKey]);

  const handleFavoritesManagerClose = () => {
    // Closing the manager should be instant; actual favorites changes
    // already dispatch 'favoritesUpdated' from the favorites utilities.
    setFavoritesManagerOpen(false);
  };

  const toggleCollapsed = () => {
    if (isRail) return;
    setSidebarCollapsed(!collapsed);
    closeDrawer();
  };

  const openDrawer = (level: 'level2' | 'level3' | 'level4', data: MenuItem) => {
    if (!collapsed) return;
    
    if (activeDrawer && level !== activeDrawer) {
      setDrawerHistory(prev => [...prev, { level: activeDrawer, data: drawerData[activeDrawer]! }]);
    }
    
    setDrawerData({
      level2: null,
      level3: null,
      level4: null,
      [level]: data,
    });
    setActiveDrawer(level);
  };

  const closeDrawer = useCallback(() => {
    setActiveDrawer(null);
    setDrawerData({ level2: null, level3: null, level4: null });
    setDrawerHistory([]);
  }, []);

  useEffect(() => {
    if (!collapsed || activeDrawer == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsed, activeDrawer, closeDrawer]);

  const goBack = () => {
    if (drawerHistory.length > 0) {
      const previousDrawer = drawerHistory[drawerHistory.length - 1];
      setDrawerHistory(prev => prev.slice(0, -1));
      setDrawerData({
        level2: null,
        level3: null,
        level4: null,
        [previousDrawer.level as 'level2' | 'level3' | 'level4']: previousDrawer.data,
      });
      setActiveDrawer(previousDrawer.level as 'level2' | 'level3' | 'level4');
    }
  };

  const handleItemClick = (item: MenuItem, hasSubitems: boolean) => {
    if (!collapsed) return;
  
    // Detect if this is a top-level menu group
    const isTopLevelItem = menuItemsWithFavorites.some((rootItem) => rootItem === item);

    if (hasSubitems) {
      if (isTopLevelItem) {
        // Always open top-level groups in the first (level2) drawer,
        // so switching between groups doesn't shift drawers further right
        openDrawer('level2', item);
        return;
      }

      // For nested groups, determine which level to open based on current drawer state
      if (!activeDrawer) {
        openDrawer('level2', item);
      } else if (activeDrawer === 'level2') {
        openDrawer('level3', item);
      } else if (activeDrawer === 'level3') {
        openDrawer('level4', item);
      }
    } else if (item.path || item.name === 'Appointments' || item.name === 'Todos') {
      navigate(
        item.name === 'Appointments'
          ? appointmentsCalendarPath()
          : item.name === 'Todos'
            ? todosSidebarPath()
            : item.path!,
      );
      closeDrawer();
      if (isMobileOpen) {
        setIsMobileOpen(false);
      }
    }
  };

  const collapsedWidth = `${COLLAPSED_RAIL_WIDTH_PX}px`;
  const expandedWidth = SIDEBAR_EXPANDED_WIDTH;

  const RAIL_HINT_STORAGE_KEY = 'app-rail-mode-hint-dismissed';
  const [showRailHint, setShowRailHint] = useState(() => {
    if (!isRail) return false;
    try {
      return localStorage.getItem(RAIL_HINT_STORAGE_KEY) !== '1';
    } catch {
      return false;
    }
  });

  const dismissRailHint = () => {
    setShowRailHint(false);
    try {
      localStorage.setItem(RAIL_HINT_STORAGE_KEY, '1');
    } catch {
      /**/
    }
  };

  const [activeTab, setActiveTab] = useState<'All' | 'Dashboard' | 'CRM' | 'Communication' | 'Settings'>('Dashboard');
  const [activeWorkspace, setActiveWorkspace] = useState<'All' | 'Today' | 'Sales' | 'Follow-up'>('Today');

  const filterRootByTab = (tab: typeof activeTab, items: MenuItem[]): MenuItem[] => {
    if (tab === 'All') return items;
    const tabMap: Record<typeof activeTab, string[]> = {
      All: [],
      Dashboard: ['Dashboard'],
      CRM: ['Address Book', 'Appointments', 'Todos'],
      Communication: ['Emails'],
      Settings: ['Settings'],
    };
    const names = tabMap[tab] ?? [];
    if (names.length === 0) return items;
    return items.filter((item) => names.includes(item.name));
  };

  const filterRootByWorkspace = (workspace: typeof activeWorkspace, items: MenuItem[]): MenuItem[] => {
    if (workspace === 'All') return items;
    const wsMap: Record<typeof activeWorkspace, string[]> = {
      All: [],
      Today: ['Dashboard', 'Todos', 'Emails', 'Address Book', 'Appointments'],
      Sales: ['Address Book', 'Appointments'],
      'Follow-up': ['Todos', 'Appointments', 'Emails'],
    };
    const names = wsMap[workspace] ?? [];
    if (names.length === 0) return items;
    return items.filter((item) => names.includes(item.name));
  };

  const rootMenuItems = useMemo(() => {
    let rootItems: MenuItem[] = menuItemsWithFavorites;

    if (sidebarStyle === 'tabs') {
      rootItems = filterRootByTab(activeTab, rootItems);
    } else if (sidebarStyle === 'workspaces') {
      rootItems = filterRootByWorkspace(activeWorkspace, rootItems);
    }

    return rootItems;
  }, [
    menuItemsWithFavorites,
    sidebarStyle,
    activeTab,
    activeWorkspace,
  ]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50"
          style={zStyle(Z_INDEX.mobileSidebarOverlay)}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={asideRef}
        className={cn(
          'fixed lg:static inset-y-0 left-0 transform transition-all duration-300 ease-in-out overflow-x-hidden min-w-0 max-w-full',
          sidebarShellClass(collapsed),
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        style={{
          width: collapsed ? collapsedWidth : expandedWidth,
          maxWidth: collapsed ? collapsedWidth : expandedWidth,
          ...zStyle(Z_INDEX.shellSidebar),
        }}
      >
        <div className="flex flex-col h-full min-w-0 overflow-hidden">
          {/* Logo/Brand */}
          <div className={sidebarHeaderClass(collapsed)}>
            {collapsed ? (
              <>
                <AppBrandIcon className="h-9 w-9 rounded-xl" iconClassName="h-4 w-4" />
                {sidebarStyle === 'classic' && (
                  <button
                    onClick={toggleCollapsed}
                    className={sidebarCollapseToggleClass()}
                    title={t('chrome.expandSidebar')}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsMobileOpen(false)}
                  className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
                  aria-label="Close menu"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <AppBrandIcon className="h-10 w-10 rounded-xl" iconClassName="h-5 w-5" />
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-bold text-white">
                      {appConfig.system.appName?.trim() || APP_BRAND.name}
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {sidebarStyle === 'classic' && (
                    <button
                      onClick={toggleCollapsed}
                      className={sidebarCollapseToggleClass()}
                      title={t('chrome.collapseSidebar')}
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setIsMobileOpen(false)}
                    className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
                  >
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>
              </>
            )}
          </div>

          <nav className={sidebarNavScrollClass(collapsed)}>
            {/* Tabs header for 'tabs' style */}
            {isTabs && (
              <div className="mb-3">
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-white/[0.05] p-1">
                  {(['All', 'Dashboard', 'CRM', 'Communication', 'Settings'] as const).map((tab) => {
                    const isActive = activeTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTab(tab)}
                        className={cn(
                          'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                          isActive
                            ? 'bg-indigo-500/25 text-white ring-1 ring-indigo-400/30'
                            : 'text-slate-400 hover:bg-white/[0.07] hover:text-slate-200',
                          tab === 'All' ? 'hidden lg:inline-flex' : 'inline-flex',
                        )}
                      >
                        {sidebarTabLabel(t, tab)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Workspaces header for 'workspaces' style */}
            {isWorkspaces && (
              <div className="mb-3">
                <div className="flex flex-wrap items-center gap-1 rounded-xl bg-white/[0.05] p-1">
                  {(['All', 'Today', 'Sales', 'Follow-up'] as const).map((ws) => {
                    const isActive = activeWorkspace === ws;
                    return (
                      <button
                        key={ws}
                        type="button"
                        onClick={() => setActiveWorkspace(ws)}
                        className={cn(
                          'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                          isActive
                            ? 'bg-indigo-500/25 text-white ring-1 ring-indigo-400/30'
                            : 'text-slate-400 hover:bg-white/[0.07] hover:text-slate-200',
                          ws === 'All' ? 'hidden lg:inline-flex' : 'inline-flex',
                        )}
                      >
                        {sidebarWorkspaceLabel(t, ws)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={sidebarNavGroupClass()}>
              {rootMenuItems.map((item, index) => {
                const isFavorites = item.name === 'Favorites';
                return (
                  <MenuItemComponent
                    key={item.path || `menu-item-${index}`}
                    item={item}
                    level={0}
                    location={location}
                    isMobileOpen={isMobileOpen}
                    setIsMobileOpen={setIsMobileOpen}
                    isFavorites={isFavorites}
                    onManageFavorites={isFavorites ? () => setFavoritesManagerOpen(true) : undefined}
                    collapsed={collapsed}
                    onItemClick={handleItemClick}
                    viewPreferences={viewPreferences || {}}
                    mutateViewPreferences={mutateViewPreferences}
                    railRightPx={railRightPx}
                  />
                );
              })}
            </div>
          </nav>

          {isRail && showRailHint && (
            <div className="mx-2 mb-2 rounded-xl border border-white/10 bg-white/[0.04] px-2.5 py-2 text-[10px] leading-snug text-slate-400">
              <p className="mb-1.5">{t('chrome.railHint')}</p>
              <button
                type="button"
                onClick={dismissRailHint}
                className="text-[10px] font-medium text-slate-300 underline hover:text-white"
              >
                {t('chrome.dismiss')}
              </button>
            </div>
          )}

          {user && (
            <>
              <SidebarUserAccount
                user={user}
                collapsed={collapsed}
                onOpenProfile={() => navigate('/preferences')}
              />
              <SidebarSignOutFooter collapsed={collapsed} onLogout={handleLogout} />
            </>
          )}

        </div>
      </aside>

      {/* Drawers for collapsed mode: one flyout, same anchor as the rail (measured sidebar right edge) */}
      {collapsed && (
        <AppSidebarFlyout
          isOpen={activeDrawer !== null}
          onClose={closeDrawer}
          item={
            activeDrawer
              ? (drawerData[activeDrawer] || { name: '', children: [] })
              : { name: '', children: [] }
          }
          leftPosition={railRightPx}
          onItemClick={handleItemClick}
          onBack={goBack}
          showBack={drawerHistory.length > 0}
          viewPreferences={viewPreferences || {}}
        />
      )}

      {/* Favorites Manager Modal */}
      <FavoritesManager
        open={favoritesManagerOpen}
        onClose={handleFavoritesManagerClose}
      />
    </>
  );
};

export default Sidebar;
